import React, { useState, useEffect, useCallback } from 'react';
import { Card, DatePicker, Space, Table, Typography, Spin, Button, Row, Col, Segmented, Select } from 'antd';
import { ReloadOutlined, BarChartOutlined, TableOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';

// Qty or Hours, for every chart and table on the tab.
let METRIC = 'qty';
const fmtVal = (v) => (METRIC === 'hours' ? Number(v || 0).toFixed(1) : (v ?? 0));

// The bar's total above a stacked bar, on its top segment, so it reads
// without hovering. Data rows carry `total`.
const totalLabel = (
  <LabelList dataKey="total" position="top" formatter={(v) => fmtVal(v)}
    style={{ fontSize: 11, fill: '#595959' }} />
);
import api from '../api';

const { RangePicker } = DatePicker;

const REASON_ORDER = [
  'Sick/Health Related', 'Jury Duty', 'Bereavement', 'Outage', 'Other',
];

const REASON_COLORS = {
  'Sick/Health Related': '#ff4d4f',
  'Jury Duty':           '#1677ff',
  'Bereavement':         '#722ed1',
  'Outage':              '#fa8c16',
  'Other':               '#8c8c8c',
};

const PERIOD_COLORS = {
  'Entire Day':               '#1677ff',
  'Multiple Consecutive Days':'#722ed1',
  'Absent Part of Day':       '#52c41a',
};

const TEAM_PALETTE = [
  '#1677ff','#52c41a','#fa8c16','#722ed1','#13c2c2','#eb2f96','#fadb14','#a0d911',
];

const fmtMonth = (mk) => dayjs(mk + '-01').format("MMM 'YY");

function PivotTable({ data, dimensions, colorMap, palette }) {
  const sortedDims = REASON_ORDER.filter((d) => dimensions.includes(d))
    .concat(dimensions.filter((d) => !REASON_ORDER.includes(d)).sort());

  const totalRow = {
    month: '__total__',
    total: data.reduce((s, r) => s + r.total, 0),
    ...sortedDims.reduce((acc, d) => ({ ...acc, [d]: data.reduce((s, r) => s + (r[d] || 0), 0) }), {}),
  };

  const tableData = [...data, totalRow];

  const getColor = (dim, idx) =>
    colorMap?.[dim] ?? palette?.[idx % palette.length] ?? '#1677ff';

  const columns = [
    {
      title: 'Month', dataIndex: 'month', key: 'month', width: 90, fixed: 'left',
      render: (v) => v === '__total__'
        ? <strong>Total</strong>
        : <span style={{ whiteSpace: 'nowrap' }}>{fmtMonth(v)}</span>,
      onCell: (r) => r.month === '__total__' ? { style: { background: '#fafafa', fontWeight: 700 } } : {},
    },
    ...sortedDims.map((dim, idx) => ({
      title: dim,
      key: dim,
      width: 130,
      align: 'right',
      render: (_, row) => {
        const v = row[dim] || 0;
        const color = getColor(dim, idx);
        if (row.month === '__total__') {
          return <strong style={{ color: v > 0 ? color : '#bfbfbf' }}>{v ? fmtVal(v) : '—'}</strong>;
        }
        return v > 0
          ? <span style={{ fontWeight: 600, color }}>{fmtVal(v)}</span>
          : <span style={{ color: '#d9d9d9' }}>—</span>;
      },
      onCell: (r) => r.month === '__total__' ? { style: { background: '#fafafa', fontWeight: 700 } } : {},
    })),
    {
      title: 'Total', key: 'total', width: 70, align: 'right', fixed: 'right',
      render: (_, row) => <strong>{fmtVal(row.total)}</strong>,
      onCell: (r) => r.month === '__total__' ? { style: { background: '#fafafa' } } : {},
    },
  ];

  return (
    <Table
      rowKey="month"
      dataSource={tableData}
      columns={columns}
      size="small"
      pagination={false}
      scroll={{ x: 'max-content' }}
      bordered
      locale={{ emptyText: 'No data.' }}
    />
  );
}

export default function AbsenceAnalytics({ teamFilter = [], setTeamFilter, teamOptions = [], lockTeam = false }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamView, setTeamView] = useState('chart');
  // Qty (number of absences) or Hours (start to end): every chart and table.
  const [metric, setMetric] = useState('qty');
  METRIC = metric;
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(6, 'month').startOf('month'),
    dayjs(),
  ]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateRange?.[0]) params.start_date = dateRange[0].startOf('month').format('YYYY-MM-DD');
    if (dateRange?.[1]) params.end_date   = dateRange[1].endOf('month').format('YYYY-MM-DD');
    if (teamFilter.length) params.team_id = teamFilter.join(',');
    if (metric === 'hours') params.metric = 'hours';
    api.get('/api/absences/analytics', { params })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateRange, teamFilter, metric]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>;
  }
  if (!data || !data.months.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <RangePicker value={dateRange} onChange={setDateRange} picker="month" format="MMM YYYY" />
          {!lockTeam && (
            <Select
              mode="multiple" allowClear showSearch optionFilterProp="label"
              placeholder="All teams" options={teamOptions}
              value={teamFilter} onChange={setTeamFilter}
              maxTagCount="responsive" style={{ minWidth: 200 }}
            />
          )}
          <Segmented
            value={metric}
            onChange={setMetric}
            options={[{ label: 'Qty', value: 'qty' }, { label: 'Hours', value: 'hours' }]}
          />
          <Button icon={<ReloadOutlined />} size="small" onClick={load}>Refresh</Button>
        </div>
        <Typography.Text type="secondary">No absence data in this date range.</Typography.Text>
      </div>
    );
  }

  const { reason_pivot, day_pivot = [], team_pivot, period_pivot, dow_pivot, reasons, teams, periods } = data;

  // Build sorted reason list preserving canonical order
  const reasonsPresent = REASON_ORDER.filter((r) => reasons.includes(r))
    .concat(reasons.filter((r) => !REASON_ORDER.includes(r)).sort());

  const teamsSorted = [...teams].sort();
  const periodsSorted = [...periods].sort();

  // Chart data for stacked-by-reason bar chart
  const reasonChartData = reason_pivot.map((r) => ({
    month: fmtMonth(r.month),
    total: r.total,
    ...reasonsPresent.reduce((acc, rn) => ({ ...acc, [rn]: r[rn] || 0 }), {}),
  }));

  // Chart data for stacked-by-team bar chart
  const teamChartData = team_pivot.map((r) => ({
    month: fmtMonth(r.month),
    total: r.total,
    ...teamsSorted.reduce((acc, t) => ({ ...acc, [t]: r[t] || 0 }), {}),
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          picker="month"
          format="MMM YYYY"
          placeholder={['From month', 'To month']}
        />
        {!lockTeam && (
          <Select
            mode="multiple" allowClear showSearch optionFilterProp="label"
            placeholder="All teams" options={teamOptions}
            value={teamFilter} onChange={setTeamFilter}
            maxTagCount="responsive" style={{ minWidth: 200 }}
          />
        )}
        <Segmented
          value={metric}
          onChange={setMetric}
          options={[{ label: 'Qty', value: 'qty' }, { label: 'Hours', value: 'hours' }]}
        />
        <Button icon={<ReloadOutlined />} size="small" onClick={load}>Refresh</Button>
      </div>

      {/* ── Absences by Reason ─────────────────────────────────────────────── */}
      <Card size="small" title="Absences by Month & Reason">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={reasonChartData} margin={{ top: 20, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={metric === 'hours'} tick={{ fontSize: 12 }} width={36} />
            <RechartTooltip formatter={(v) => fmtVal(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {reasonsPresent.map((reason, i) => (
              <Bar key={reason} dataKey={reason} stackId="a"
                fill={REASON_COLORS[reason] ?? '#8c8c8c'} maxBarSize={60}>
                {i === reasonsPresent.length - 1 && totalLabel}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* ── Absences by Day & Reason (start date) ─────────────────────────── */}
      <Card size="small" title="Absences by Day & Reason">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={day_pivot.map((r) => ({ ...r, day: dayjs(r.day).format('MMM D') }))}
            margin={{ top: 20, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={metric === 'hours'} tick={{ fontSize: 12 }} width={36} />
            <RechartTooltip formatter={(v) => fmtVal(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {reasonsPresent.map((reason, i) => (
              <Bar key={reason} dataKey={reason} stackId="a"
                fill={REASON_COLORS[reason] ?? '#8c8c8c'} maxBarSize={40}>
                {i === reasonsPresent.length - 1 && totalLabel}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* ── Pivot: Month × Reason ──────────────────────────────────────────── */}
      <Card size="small" title="Pivot: Month × Reason">
        <PivotTable
          data={reason_pivot}
          dimensions={reasonsPresent}
          colorMap={REASON_COLORS}
        />
      </Card>

      {/* ── By Team ────────────────────────────────────────────────────────── */}
      <Card
        size="small"
        title="Absences by Team"
        extra={
          <Segmented
            size="small"
            value={teamView}
            onChange={setTeamView}
            options={[
              { label: 'Chart', value: 'chart', icon: <BarChartOutlined /> },
              { label: 'Pivot', value: 'pivot', icon: <TableOutlined /> },
            ]}
          />
        }
      >
        {teamView === 'chart' ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={teamChartData} margin={{ top: 20, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={metric === 'hours'} tick={{ fontSize: 12 }} width={36} />
              <RechartTooltip formatter={(v) => fmtVal(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {teamsSorted.map((team, idx) => (
                <Bar key={team} dataKey={team} stackId="a"
                  fill={TEAM_PALETTE[idx % TEAM_PALETTE.length]} maxBarSize={60}>
                  {idx === teamsSorted.length - 1 && totalLabel}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <PivotTable
            data={team_pivot}
            dimensions={teamsSorted}
            palette={TEAM_PALETTE}
          />
        )}
      </Card>

      {/* ── By Absence Period ──────────────────────────────────────────────── */}
      <Card size="small" title="Pivot: Month × Absence Period">
        <PivotTable
          data={period_pivot}
          dimensions={periodsSorted}
          colorMap={PERIOD_COLORS}
        />
      </Card>

      {/* ── By Day of Week ─────────────────────────────────────────────────── */}
      {dow_pivot && (
        <Card size="small" title="Absences by Day of Week">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={dow_pivot} margin={{ top: 20, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="dow" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={metric === 'hours'} tick={{ fontSize: 12 }} width={36} />
              <RechartTooltip formatter={(v) => fmtVal(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {reasonsPresent.map((reason, i) => (
                <Bar key={reason} dataKey={reason} stackId="a"
                  fill={REASON_COLORS[reason] ?? '#8c8c8c'} maxBarSize={60}>
                  {i === reasonsPresent.length - 1 && totalLabel}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
